import { useState } from "react";
import { useQuery, useMutation } from "@apollo/client/react";
import { formatOneworld } from "@app/components/oneworld-word";
import { useEventsAdminDemo } from "@app/lib/events-admin-demo-context";
import {
  GET_PRODUCT_SETUP,
  TOGGLE_PRODUCT_FEATURE,
  SET_LEAD_PRODUCT,
} from "@app/graphql/events-admin.queries";
import { EyeIcon, SettingsIcon } from "@app/lib/events-admin-icons";
import {
  Button,
  Toggle,
} from "@its-engineering-and-development/ui-foundation/components";
import "./settings-page.css";

function isTravelFeature(id: string) {
  return ["flights", "hotel", "transportation", "travel-insurance"].includes(
    id,
  );
}

function iconLabel(id: string) {
  if (id === "flights") {
    return "Air";
  }

  if (id === "hotel") {
    return "Hotel";
  }

  if (id === "transportation") {
    return "Car";
  }

  if (id === "travel-insurance") {
    return "Shield";
  }

  return "Feature";
}

interface ToggleProductFeatureMutationData {
  toggleProductFeature: { leadProduct: string };
}

interface SetLeadProductMutationData {
  setLeadProduct: { leadProduct: string };
}

export function SettingsPage() {
  const {
    productSetup: localProductSetup,
    setLeadProduct: setLeadProductLocal,
    toggleFeature: toggleFeatureLocal,
  } = useEventsAdminDemo();

  // GraphQL: fetch product setup from mock server
  const { data: gqlData, refetch } = useQuery<{
    productSetup: typeof localProductSetup;
  }>(GET_PRODUCT_SETUP, { fetchPolicy: "network-only" });
  const gqlProductSetup = gqlData?.productSetup;
  const productSetup = gqlProductSetup?.products
    ? { ...localProductSetup, ...gqlProductSetup }
    : localProductSetup;

  const [toggleFeatureMutation] = useMutation<ToggleProductFeatureMutationData>(
    TOGGLE_PRODUCT_FEATURE,
  );
  const [setLeadProductMutation] =
    useMutation<SetLeadProductMutationData>(SET_LEAD_PRODUCT);

  const setLeadProduct = (featureId: string) => {
    setLeadProductLocal(featureId);
    setLeadProductMutation({ variables: { featureId } }).then(({ data }) => {
      setLeadProductLocal(data?.setLeadProduct?.leadProduct ?? featureId);
      void refetch();
    });
  };

  const toggleFeature = (featureId: string) => {
    toggleFeatureLocal(featureId);
    const feature = productSetup.products.find((f) => f.id === featureId);
    const newEnabled = !(feature?.enabled ?? false);
    toggleFeatureMutation({
      variables: { featureId, enabled: newEnabled },
    }).then(({ data }) => {
      if (data?.toggleProductFeature?.leadProduct) {
        setLeadProductLocal(data.toggleProductFeature.leadProduct);
      }
      void refetch();
    });
  };

  const [configLevel, setConfigLevel] = useState<"airline" | "event">(
    "airline",
  );
  const [activeTab, setActiveTab] = useState<
    "products" | "questions" | "templates"
  >("products");
  const [saveLabel, setSaveLabel] = useState("Save Configuration");
  const [previewActive, setPreviewActive] = useState(false);

  const travelFeatures = productSetup.products.filter(
    (feature) => feature.category === "travel",
  );
  const invitationFeatures = productSetup.products.filter(
    (feature) => feature.category === "invitation",
  );
  const eventFeatures = productSetup.products.filter(
    (feature) => feature.category === "event",
  );
  const activeFeatures = productSetup.products.filter(
    (feature) => feature.enabled,
  );

  return (
    <section className="events-page events-page--stack settings-page">
      <header className="events-page__hero">
        <div className="events-page__hero-title">
          <div className="events-page__hero-icon">
            <SettingsIcon className="size-5" />
          </div>
          <div>
            <p className="events-page__eyebrow">
              {formatOneworld("oneworld Admin")}
            </p>
            <h1 className="events-page__title">Event Product Setup</h1>
          </div>
        </div>

        <div className="events-page__hero-actions">
          <div className="events-segmented">
            <button
              type="button"
              aria-pressed={configLevel === "airline"}
              className={configLevel === "airline" ? "is-active" : ""}
              onClick={() => setConfigLevel("airline")}
            >
              Airline Level
            </button>
            <button
              type="button"
              aria-pressed={configLevel === "event"}
              className={configLevel === "event" ? "is-active" : ""}
              onClick={() => setConfigLevel("event")}
            >
              Event Level
            </button>
          </div>
          <Button
            size="lg"
            color="primary"
            className="events-action-button"
            onPress={() => {
              setSaveLabel("Saved!");
              setTimeout(() => setSaveLabel("Save Configuration"), 2000);
            }}
          >
            {saveLabel}
          </Button>
        </div>
      </header>

      <div className="events-page__hero events-page__hero--table">
        <div
          className="events-chip-group"
          role="tablist"
          aria-label="Configuration tabs"
        >
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "products"}
            className={`events-chip-tab ${activeTab === "products" ? "is-active" : ""}`}
            onClick={() => setActiveTab("products")}
          >
            Products
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "questions"}
            className={`events-chip-tab ${activeTab === "questions" ? "is-active" : ""}`}
            onClick={() => setActiveTab("questions")}
          >
            Questions
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "templates"}
            className={`events-chip-tab ${activeTab === "templates" ? "is-active" : ""}`}
            onClick={() => setActiveTab("templates")}
          >
            Templates
          </button>
        </div>

        <Button
          size="lg"
          color="secondary"
          className="events-outline-button"
          onPress={() => {
            setPreviewActive(true);
            setTimeout(() => setPreviewActive(false), 2000);
          }}
        >
          <EyeIcon className="size-4" />
          {previewActive ? "Preview Active" : "Preview"}
        </Button>
      </div>

      <div className="events-settings-grid">
        <article className="events-surface">
          <section className="events-settings-section">
            <header className="events-settings-section__header">
              <div>
                <h2 className="events-surface__title">Travel Products</h2>
                <p className="events-page__subtitle">
                  Enable or disable products for this configuration.
                </p>
              </div>
              <span className="events-tone events-tone--info">
                Active Products{" "}
                {travelFeatures.filter((feature) => feature.enabled).length}
              </span>
            </header>

            <div className="events-settings-card-grid">
              {travelFeatures.map((feature) => (
                <article
                  key={feature.id}
                  className={`events-settings-card ${feature.enabled ? "is-enabled" : ""}`}
                >
                  <div className="events-settings-card__icon">
                    {iconLabel(feature.id)}
                  </div>
                  <div>
                    <strong>{feature.label}</strong>
                    <p>{feature.description}</p>
                  </div>
                  <Toggle
                    aria-label={`Toggle ${feature.label}`}
                    isSelected={feature.enabled}
                    onChange={() => toggleFeature(feature.id)}
                    size="sm"
                    slim
                  />
                </article>
              ))}
            </div>
          </section>

          <section className="events-settings-section">
            <header className="events-settings-section__header">
              <div>
                <h2 className="events-surface__title">Lead Product</h2>
                <p className="events-page__subtitle">
                  Define which product is the primary focus for event
                  organizers.
                </p>
              </div>
            </header>

            <div className="events-settings-lead-row">
              {travelFeatures
                .filter((feature) => isTravelFeature(feature.id))
                .map((feature) => (
                  <button
                    key={feature.id}
                    type="button"
                    className={`events-settings-lead-button ${productSetup.leadProduct === feature.id ? "is-active" : ""}`}
                    onClick={() => setLeadProduct(feature.id)}
                  >
                    Lead: {feature.label}
                  </button>
                ))}
            </div>
          </section>

          <section className="events-settings-section">
            <header className="events-settings-section__header">
              <div>
                <h2 className="events-surface__title">Invitation Features</h2>
                <p className="events-page__subtitle">
                  Control which invitation methods and features are available to
                  event organizers.
                </p>
              </div>
            </header>

            <div className="events-settings-card-grid">
              {invitationFeatures.map((feature) => (
                <article
                  key={feature.id}
                  className={`events-settings-card ${feature.enabled ? "is-enabled" : ""}`}
                >
                  <div className="events-settings-card__icon">
                    {iconLabel(feature.id)}
                  </div>
                  <div>
                    <strong>
                      {feature.label}
                      {feature.premium ? (
                        <span className="events-settings-card__badge">
                          Premium
                        </span>
                      ) : null}
                    </strong>
                    <p>{feature.description}</p>
                  </div>
                  <Toggle
                    aria-label={`Toggle ${feature.label}`}
                    isSelected={feature.enabled}
                    onChange={() => toggleFeature(feature.id)}
                    size="sm"
                    slim
                  />
                </article>
              ))}
            </div>

            <div className="events-premium-note">
              Premium features allow you to upcharge clients for advanced
              invitation capabilities.
            </div>
          </section>

          <section className="events-settings-section">
            <header className="events-settings-section__header">
              <div>
                <h2 className="events-surface__title">Event Features</h2>
                <p className="events-page__subtitle">
                  Enable or disable specific event features for organizers.
                </p>
              </div>
            </header>

            <div className="events-settings-card-grid">
              {eventFeatures.map((feature) => (
                <article
                  key={feature.id}
                  className={`events-settings-card ${feature.enabled ? "is-enabled" : ""}`}
                >
                  <div className="events-settings-card__icon">
                    {iconLabel(feature.id)}
                  </div>
                  <div>
                    <strong>{feature.label}</strong>
                    <p>{feature.description}</p>
                  </div>
                  <Toggle
                    aria-label={`Toggle ${feature.label}`}
                    isSelected={feature.enabled}
                    onChange={() => toggleFeature(feature.id)}
                    size="sm"
                    slim
                  />
                </article>
              ))}
            </div>
          </section>
        </article>

        <aside className="events-surface events-surface--summary">
          <header className="events-summary-card__header">
            <div className="events-page__hero-icon">
              <SettingsIcon className="size-4" />
            </div>
            <h2 className="events-surface__title">Configuration Summary</h2>
          </header>

          <section className="events-summary-card__section">
            <h3>Active Products</h3>
            <ul>
              {activeFeatures
                .filter((feature) => feature.category === "travel")
                .map((feature) => (
                  <li key={feature.id}>{feature.label}</li>
                ))}
            </ul>
          </section>

          <section className="events-summary-card__section">
            <h3>Lead Product</h3>
            <p className="events-summary-card__lead">
              {travelFeatures.find(
                (feature) => feature.id === productSetup.leadProduct,
              )?.label ?? "Flights"}
            </p>
          </section>

          <section className="events-summary-card__section">
            <h3>Active Questions</h3>
            <ul>
              {productSetup.questionSummary.map((question) => (
                <li key={question.label}>
                  <span>{question.label}</span>
                  <span>{question.count}</span>
                </li>
              ))}
            </ul>
          </section>

          <p className="events-page__subtitle">
            This configuration determines what organizers see during event
            creation and what travelers see during registration.
          </p>
        </aside>
      </div>
    </section>
  );
}
